/** @jsx dom */
import bus from '@viskan/bus';
import {Spinner} from '@viskan/estore-components';
import debounce from 'debounce';
import deepEqual from 'deep-equal';
import dom from 'magic-virtual-element';
import {Cell, Grid} from 'viskan-deku-grid';
import {trackCheckout} from '~/analytics';
import {Image} from '~/media';
import Shopcart from '~/shopcart';
import {Section} from '~/streamline-cms';
import Vco from '~/vco';
import Embedded, {getType} from './embedded';
import Shipping from './shipping';

const name = 'Checkout';

const initialState = () => ({
	trackCheckout: true
});

const INTEGRITIES_EMAIL_CONSENT_NAMES = ['EMAIL_INFO', 'TM'];
const isEmbeddedPaymentMethod = method => method && method.method.methodCode.toLowerCase() === 'dibs easy';
const isValidDeliveryPoint = (shipping, activeDeliveryMethod) => {
	return activeDeliveryMethod && (activeDeliveryMethod.deliveryPointRule !== 'MANDATORY' || (shipping.chosenCity && shipping.chosenDeliveryPoint));
};

const renderCheckoutMethod = (props, activeDeliveryMethod, activePaymentMethod) => {
	const {checkout, fetch, shopcart, specialParameters, locale, router} = props;

	const type = getType(specialParameters);
	const setPerson = props.customer.logged_in_level === 0 && props.setPerson;

	if (!type || !isEmbeddedPaymentMethod(activePaymentMethod)) {
		const paymentMethods = checkout.paymentMethods.filter(method => method.method.methodCode !== 'DIBS Easy');
		const checkoutWithoutEmbedded = {...checkout, paymentMethods};

		return <Vco checkout={checkoutWithoutEmbedded} customer={props.customer} setPerson={setPerson} router={router}/>;
	}

	const isSuspended = props.isPending || !isValidDeliveryPoint(checkout.shipping, activeDeliveryMethod);

	return <Embedded type={type} isSuspended={isSuspended} shopcart={shopcart} fetch={fetch} params={specialParameters} locale={locale}/>;
};
const renderCheckoutMethods = (props, activeDeliveryMethod, activePaymentMethod) => {
	const {paymentMethods} = props.checkout;
	const embeddedMethod = paymentMethods.find(isEmbeddedPaymentMethod);
	const vcoPaymentMethods = paymentMethods.filter(method => !isEmbeddedPaymentMethod(method));
	const isEmbeddedActive = isEmbeddedPaymentMethod(activePaymentMethod);
	const country = props.countryCode;
	const onPaymentClick = paymentMethod => () => props.updateShopcart({
		shopcart_id_str: props.shopcart.head.shopcart_id_str,
		pay_id: paymentMethod.method.paymentMethodId
	});

	if (!embeddedMethod || vcoPaymentMethods.length === 0) {
		return renderCheckoutMethod(props);
	}

	if (country === 'NO' || country === 'FI') {
		return (
			<div class='Checkout-methods'>
				<div class='Checkout-tabs'>
					<div class={['Checkout-tab', !isEmbeddedActive && 'isActive']} onClick={onPaymentClick(vcoPaymentMethods[0])}>
						{vcoPaymentMethods.length > 1 ? props.getTranslation('checkout.vco') : vcoPaymentMethods[0].method.methodDescription || vcoPaymentMethods[0].method.methodName}
					</div>
					<div class={['Checkout-tab', isEmbeddedActive && 'isActive']} onClick={onPaymentClick(embeddedMethod)}>{embeddedMethod.method.methodDescription || embeddedMethod.method.methodName}</div>
				</div>
				<div class='Checkout-method'>
					{renderCheckoutMethod(props, activeDeliveryMethod, activePaymentMethod)}
				</div>
			</div>
		);
	}

	return (
		<div class='Checkout-methods'>
			<div class='Checkout-tabs'>
				<div class={['Checkout-tab', isEmbeddedActive && 'isActive']} onClick={onPaymentClick(embeddedMethod)}>{embeddedMethod.method.methodDescription || embeddedMethod.method.methodName}</div>
				<div class={['Checkout-tab', !isEmbeddedActive && 'isActive']} onClick={onPaymentClick(vcoPaymentMethods[0])}>
					{vcoPaymentMethods.length > 1 ? props.getTranslation('checkout.vco') : vcoPaymentMethods[0].method.methodDescription || vcoPaymentMethods[0].method.methodName}
				</div>
			</div>
			<div class='Checkout-method'>
				{renderCheckoutMethod(props, activeDeliveryMethod, activePaymentMethod)}
			</div>
		</div>
	);
};

const handleIntegritiesConsentChange = props => e => {
	const integrityNames = e.target.name.split(',');
	const integrityConsent = e.target.checked;

	props.setIntegrities({integrityNames, integrityConsent});
};

const renderIntegrityEmailConsent = props => {
	const id = INTEGRITIES_EMAIL_CONSENT_NAMES.join(',').replace(/,/g, '-').toLowerCase();

	return (
		<div class='Checkout-email-consent'>
			<Grid gutter>
				<Cell size='1of12'>
					<div class='Checkout-email-consent-input'>
						<input type='checkbox' id={id} name={INTEGRITIES_EMAIL_CONSENT_NAMES} onChange={debounce(handleIntegritiesConsentChange(props), 2000)}/>
					</div>
				</Cell>
				<Cell size='11of12'>
					<div class='Checkout-email-consent-label'>
						<label for={id} innerHTML={props.getTranslation(`checkout.email_consent_label`)}/>
					</div>
				</Cell>
			</Grid>
		</div>
	);
};

const getKCOCallbackError = props => {
	const errorMapping = {
		'missing-balance': 'checkout.error.missing-balance',
		'missing-deliverypoint-id': 'checkout.error.missing-deliverypoint-id',
		'no-shopcart': 'checkout.error.no-shopcart'
	};

	const errors = Object.entries(props.router.query)
		.filter(parameter => errorMapping[parameter[0]])
		.map(parameter => <div key={parameter[0]} class={`${name}-kcoCallbackError`} innerHTML={props.getTranslation(errorMapping[parameter[0]])}/>);

	return <div class={`${name}-kcoCallbackErrors`}>{errors}</div>;
};

const cancelPurchaseAndRestoreCart = purchaseNumber => v12.util.http.post('/api/purchase-cancellations', {body: {purchaseNumber}})
	.then(({body}) => v12.util.http.post('/api/shopcart-restore', {body, json: false, query: v12.options.defaults}))
	.then(() => v12.shopcart.get())
	.then(data => {
		bus.emit('shopcart:update', data);
		return data;
	})
	.then(data => {
		bus.emit('shopcart:get', data);
	});

const afterMount = ({props}) => {
	const {contentManagementActive, fetch, query, purchaseNumber} = props;
	const country = props.countryCode;

	if (purchaseNumber && query && query.includes('cancel')) {
		cancelPurchaseAndRestoreCart(purchaseNumber);
	}

	fetch({
		pathPrefix: props.router.pathPrefix,
		googleAnalyticsId: props.analyticsClientId,
		integrities: props.integrities
	}).then(payload => {
		const embedded = payload.value.paymentMethods.find(isEmbeddedPaymentMethod);

		if (embedded && (country === 'DK' || country === 'SE')) {
			props.updateShopcart({
				shopcart_id_str: props.shopcart.head.shopcart_id_str,
				pay_id: embedded.method.paymentMethodId
			});
		}
	});

	if (!contentManagementActive) {
		props.categoryFetch(query, 'main');
	}
};

const afterUpdate = ({props, state}, prevProps, prevState, setState) => {
	if (!deepEqual(props.shopcart, prevProps.shopcart) || (props.analyticsClientId !== prevProps.analyticsClientId) || props.integrities.length !== prevProps.integrities.length) {
		props.fetch({pathPrefix: props.router.pathPrefix, googleAnalyticsId: props.analyticsClientId, integrities: props.integrities});
	}

	if (state.trackCheckout && props.shopcart.rows.length > 0) {
		trackCheckout(props.shopcart);
		setState({trackCheckout: false});
	}
};

const render = ({props}) => {
	const {checkout, getTranslation, shopcart, shopcartAvailable} = props;
	const validShopcart = shopcartAvailable && shopcart && shopcart.rows && shopcart.rows.length > 0;
	const activeDeliveryMethod = checkout.deliveryMethods.find(method => method.method.deliveryMethodId === shopcart.outorder.wdel_id);
	const activePaymentMethod = checkout.paymentMethods.find(method => method.method.paymentMethodId === shopcart.outorder.pay_id);
	const method = validShopcart && renderCheckoutMethods(props, activeDeliveryMethod, activePaymentMethod);

	return (
		<div class={[name, !shopcartAvailable && `${name}--empty`]}>
			{props.isPending && (
				<div class={['Spinner-container', props.class]}>
					<Spinner/>
				</div>
			)}
			<Section sectionId='main'/>
			<div class='Container'>
				<div class={`${name}-content`}>
					{!validShopcart && getTranslation('shopcart.empty')}
					{(validShopcart && typeof props.router.query['validation-failure'] !== 'undefined') && getKCOCallbackError(props)}
					<Grid>
						<Cell lg-size='3of5' md-size='full' sm-size='full'>
							<div class='ShopcartChekoutContainer'>
								<div class='usp-container usp-container-desktop u-textCenter'>
									<Grid gutter>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='subscribers.svg' alt='' height='50' width='50'/></div>
											<p>{getTranslation('checkout.usp_save')}</p>
										</Cell>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='lock-unlock.png' alt='' height='50' width='50'/></div>
											<p>{getTranslation('checkout.usp_subscribers')}</p>
										</Cell>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='returns.svg' alt='' height='45' width='45'/></div>
											<p>{getTranslation('checkout.usp_returns')}</p>
										</Cell>
									</Grid>
								</div>
								{validShopcart && <Shopcart/>}
								{validShopcart && renderIntegrityEmailConsent(props)}
								{validShopcart && <Shipping {...props} method={activeDeliveryMethod.method}/>}
								<div class='usp-container usp-container-mobile u-textCenter'>
									<Grid>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='subscribers.svg' alt='' height='60' width='60'/></div>
											<p>{getTranslation('checkout.usp_save')}</p>
										</Cell>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='lock-unlock.png' alt='' height='60' width='60'/></div>
											<p>{getTranslation('checkout.usp_subscribers')}</p>
										</Cell>
										<Cell md-size='1of3' sm-size='1of3'>
											<div class='Usp-image-size u-textCenter'><Image source='returns.svg' alt='' height='60' width='60'/></div>
											<p>{getTranslation('checkout.usp_returns')}</p>
										</Cell>
									</Grid>
								</div>
							</div>
						</Cell>
						<Cell lg-size='2of5' md-size='full' sm-size='full'>
							<div class='Payment'>
								{validShopcart && <h2 class='Heading Payment-heading'>{getTranslation('checkout.payment')}</h2>}
								{method}
							</div>
						</Cell>
					</Grid>
				</div>
			</div>
		</div>
	);
};

export default {afterMount, afterUpdate, initialState, name, render};
